import Seo from "@/components/feature/Seo";
import Container from "@/components/base/Container";
import PageHero from "@/components/base/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Button from "@/components/base/Button";
import { companyStory, equipo, hitos } from "@/mocks/about";
import { corporativoIntro, mision, vision, valoresCorporativos } from "@/mocks/corporativo";
import { stats } from "@/mocks/site";

export default function Nosotros() {
  return (
    <>
      <Seo
        title="Sobre nosotros"
        description="Somos AXONIA GROUP: una empresa de transporte y logística con flota moderna, equipo profesional y coordinación personalizada en todo el Perú."
        path="/nosotros"
      />
      <PageHero
        title="Somos AXONIA GROUP"
        subtitle="Una empresa de transporte y logística que combina una flota moderna, equipo profesional y coordinación personalizada."
        crumbs={[{ label: "Inicio", to: "/" }, { label: "Nosotros" }]}
        image="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20modern%20logistics%20terminal%20with%20white%20trucks%20parked%20in%20rows%20and%20workers%20coordinating%20operations%20at%20blue%20hour%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-nosotros&orientation=landscape"
      />

      <section className="bg-background-50 py-14 md:py-20">
        <Container>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="animate-fade-right">
              <SectionHeading
                eyebrow={companyStory.eyebrow}
                title={companyStory.titulo}
              />
              <div className="mt-5 space-y-4">
                {companyStory.parrafos.map((parrafo, index) => (
                  <p key={index} className="text-sm leading-relaxed text-foreground-600 md:text-base">
                    {parrafo}
                  </p>
                ))}
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button to="/solicitar-servicio" variant="primary" size="md" icon="ri-send-plane-line">
                  Solicitar servicio
                </Button>
                <Button to="/flota" variant="outline" size="md" icon="ri-truck-line">
                  Conocer la flota
                </Button>
              </div>
            </div>

            <div className="relative animate-fade-up">
              <div className="relative h-[280px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
                <img
                  src={companyStory.imagen_url}
                  alt="Equipo y flota de AXONIA GROUP en el terminal de operaciones"
                  title="AXONIA GROUP — operaciones y flota"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 left-4 hidden rounded-lg border border-background-200 bg-background-50 px-5 py-4 sm:block">
                <p className="text-2xl font-heading font-bold text-primary-600">18+</p>
                <p className="text-xs font-label text-foreground-500">años de experiencia</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="Nuestra trayectoria"
            title="Un recorrido de crecimiento constante"
            description="Comenzamos con una pequeña flota y hoy operamos cobertura nacional con unidades para cada necesidad."
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {hitos.map((hito, index) => (
              <div
                key={hito.id}
                className="relative rounded-lg border border-background-200 bg-background-50 p-5 animate-fade-up"
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-flag-line text-lg leading-none" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-xl font-heading font-bold text-foreground-950">{hito.valor}</p>
                <p className="mt-1 text-sm text-foreground-600">{hito.etiqueta}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="AXONIA GROUP"
            title="Misión, Visión y Valores Corporativos"
            description={corporativoIntro}
          />

          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {[mision, vision].map((pilar, index) => (
              <article
                key={pilar.id}
                className="relative overflow-hidden rounded-lg border border-background-200 bg-background-50 p-6 animate-fade-up md:p-8"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <span className="pointer-events-none absolute right-5 top-4 text-6xl font-heading font-bold leading-none text-background-200 md:text-7xl">
                  {pilar.numero}
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${pilar.icono} text-2xl leading-none`} aria-hidden="true"></i>
                </span>
                <p className="mt-5 text-xs font-label font-semibold uppercase tracking-wider text-accent-700">
                  {pilar.etiqueta}
                </p>
                <h3 className="mt-1 text-xl font-heading font-bold text-foreground-950 md:text-2xl">
                  {pilar.titulo}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {pilar.descripcion}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-12">
            <div className="flex items-center gap-4">
              <span className="text-3xl font-heading font-bold text-background-300">03</span>
              <div>
                <p className="text-xs font-label font-semibold uppercase tracking-wider text-accent-700">
                  Pilares Éticos y Operativos
                </p>
                <h3 className="mt-1 text-xl font-heading font-bold text-foreground-950 md:text-2xl">
                  Valores Corporativos
                </h3>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {valoresCorporativos.map((valor, index) => (
                <article
                  key={valor.id}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 animate-fade-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                    <i className={`${valor.icono} text-xl leading-none`} aria-hidden="true"></i>
                  </span>
                  <h4 className="mt-4 text-base font-heading font-semibold text-foreground-950">
                    {valor.titulo}
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {valor.descripcion}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-primary-950 py-14 md:py-20">
        <Container>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50/10 text-accent-300">
                  <i className={`${stat.icono} text-xl leading-none`} aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-2xl font-heading font-bold text-primary-50 md:text-3xl">
                  {stat.valor}
                </p>
                <p className="mt-1 text-xs font-label text-primary-200 md:text-sm">
                  {stat.etiqueta}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="Nuestro equipo"
            title="Personas detrás de cada operación"
            description="Un equipo comprometido que coordina, conduce y cuida cada servicio de principio a fin."
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {equipo.map((miembro, index) => (
              <article
                key={miembro.id}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 animate-fade-up"
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <div className="h-64 w-full overflow-hidden bg-background-100">
                  <img
                    src={miembro.imagen_url}
                    alt={`${miembro.nombre}, ${miembro.cargo} de AXONIA GROUP`}
                    title={`${miembro.nombre} — ${miembro.cargo}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-heading font-semibold text-foreground-950">
                    {miembro.nombre}
                  </h3>
                  <p className="mt-1 text-xs font-label font-semibold uppercase tracking-wider text-accent-700">
                    {miembro.cargo}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {miembro.descripcion}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <Container>
          <div className="flex flex-col items-center gap-6 rounded-lg border border-background-200 bg-background-50 px-6 py-10 text-center md:px-12">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-team-line text-2xl leading-none" aria-hidden="true"></i>
            </span>
            <SectionHeading
              align="center"
              title="Trabajemos juntos"
              description="Cuéntanos qué necesitas mover y te ayudamos a elegir la unidad y el servicio más adecuados."
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button to="/solicitar-servicio" variant="primary" size="lg" icon="ri-send-plane-line">
                Solicitar servicio
              </Button>
              <Button to="/contacto" variant="outline" size="lg" icon="ri-customer-service-2-line">
                Ir a contacto
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}